/**
 * GCSA 法律顾问 — 九语固化快照（与主站 legalCounsel.json 一致）
 * 供 SiteFooter 顾问入口与弹窗使用，不依赖 Nuxt / 宿主 i18n。
 *
 * 维护：在 gcsa-ui 目录运行 `node scripts/sync-legal-counsel-from-gcsa.mjs`
 */

import type { SiteFooterLocale } from './gcsaFooterContent';

export type GcsaLegalCounselFirmId = 'leelee' | 'fn' | 'pmt' | 'yuanshu';

export type GcsaLegalCounselFirm = {
  id: GcsaLegalCounselFirmId;
  title: string;
  englishName: string;
  location: string;
  address: string;
  excerpt: string;
  focus: string[];
  intro: string[];
  disclaimer?: string;
};

/** 顺序与主站 LEGAL_COUNSEL_FIRMS 一致：leelee → fn → pmt → yuanshu */
export type GcsaLegalCounselFirms = readonly [
  GcsaLegalCounselFirm,
  GcsaLegalCounselFirm,
  GcsaLegalCounselFirm,
  GcsaLegalCounselFirm
];

export type GcsaLegalCounselSnapshot = {
  /** 页脚顾问区标题（主站 legalCounsel.homeTitle） */
  title: string;
  closeModal: string;
  addressLabel: string;
  focusLabel: string;
  introTitle: string;
  /** aria 模板，含 `{name}` 占位符 */
  openIntro: string;
  firms: GcsaLegalCounselFirms;
};

const GCSA_LEGAL_COUNSEL_ZH_CN: GcsaLegalCounselSnapshot = {
  title: "GCSA 法律顾问",
  closeModal: "关闭",
  addressLabel: "办公地址",
  focusLabel: "擅长领域",
  introTitle: "机构简介",
  openIntro: "查看{name}简介",
  firms: [
    {
      id: "leelee",
      title: "新加坡 李及李律师馆",
      englishName: "Lee & Lee LLP",
      location: "新加坡 · 桥北路",
      address: "新加坡：25 North Bridge Road, Level 7, Singapore 179104（邻近最高法院）",
      excerpt: "新加坡领先的全方位法律事务所 · 自1955年创立至今七十余年，以诚信、专业与战略洞察力保护客户利益。",
      focus: ["银行与金融", "公司事务", "知识产权", "诉讼争议", "房地产", "跨境业务"],
      intro: [
        "李及李律师馆于1955年9月1日在新加坡创立，由李光耀先生、柯玉芝女士及李金耀先生共同创办，现办公地点位于桥北路25号7楼。本所植根新加坡七十余年，为个人、企业、金融机构及公共机构提供严谨、正直且具国际视野的法律服务。",
        "本所由创始人奠定的职业操守传统延续至今，将深厚的普通法实务与超过七十年的庭审及交易经验融为一体，以英、中等语言为跨境客户提供服务，秉承“立足历史经验，顺应未来趋势”的理念，为现代商业与家族客户带来经得起时间考验的法律解决方案。",
        "业务涵盖银行与金融、公司与并购、知识产权、诉讼与争议解决、房地产与物业及各类专项法律领域。如今本所已发展为拥有逾180名律师、律师助理及支持人员的全方位律师行，并曾与国际律师事务所设立新加坡历时最久的联合法律合伙，服务三代以上的家族客户及众多上市公司与跨国集团。",
      ],
      disclaimer: "上述常年法律顾问关系并不构成 Lee & Lee LLP 对 GCSA 的业务、活动、运营、产品或服务作出的任何背书、支持、批准、核实、保证、担保、陈述或承诺，亦不代表 Lee & Lee LLP 直接参与 GCSA 的任何业务、活动、运营、产品或服务。"
    },
    {
      id: "fn",
      title: "葡萄牙&澳门   弗朗西斯科·尼古劳博士律师事务所",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "里斯本 · 澳门",
      address: "里斯本：Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal；澳门：1/F, No. 194 Xinma Road, Macau",
      excerpt: "里斯本与澳门全方位法律事务所 · 横跨葡萄牙与澳门三十余年的卓越法律实践。",
      focus: ["民法", "刑法", "跨境诉讼"],
      intro: [
        "弗朗西斯科·尼古劳博士律师事务所在里斯本与澳门设有办公室，植根于横跨葡萄牙与澳门三十余年的卓越法律实践，为个人、企业、机构及公共部门提供严谨、正直且具国际视野的法律咨询。",
        "在弗朗西斯科·尼古劳博士——法学博士、资深出庭律师——的带领下，本所将深厚的法理学素养与三十六年的庭审经验融为一体，以葡、英、法、中及拉丁五种语言提供服务，为现代跨境客户带来严谨的大陆法系传统。",
        "业务涵盖民法、刑法、劳动法、行政法、民事诉讼与宪法等领域，并在葡萄牙与澳门各级法院拥有超过三十六年的出庭经验。",
      ]
    },
    {
      id: "pmt",
      title: "香港麦家荣律师行",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "香港 · 中环",
      address: "香港中环干诺道中 111 号永安中心 9 楼",
      excerpt: "扎根香港、放眼海外的综合性律师行，提供诉讼与非诉讼全方位多元化法律服务。",
      focus: ["企业融资", "商业及民事诉讼", "内地委托公证人"],
      intro: [
        "香港麦家荣律师行是一家扎根于香港、放眼于海外且具规模的综合性律师事务所，能够提供诉讼和非诉讼全方位多元化服务。",
        "自成立以来，香港麦家荣律师行在企业融资、商业及民事诉讼、内地委托公证人服务等核心法律服务中业绩卓著，并以务实的办事态度、专业的法律知识和丰富的行业经验，为各地客户提供高效率及高质量的法律服务。",
        "2024 年，香港麦家荣律师行荣膺 MYTHFOCUS 所颁发亚洲最有价值品牌大奖中的“大中华年度最佳法律顾问服务”奖项，展现了其不仅为客户提供超出预期的服务与优质体验，更具备预判及克服商业问题、持续提升服务能力以及对行业与社会的不懈贡献。",
        "在持续服务客户的过程中，香港麦家荣律师行与相关监管部门及国内外众多证券公司、投资银行、会计师事务所、资产评估机构及其他律师事务所建立了良好的业务联系，并积累了与政府机关及监管部门沟通协作的丰富经验，能够发挥优势、整合资源，为客户提供优质高效的法律服务。",
      ]
    },
    {
      id: "yuanshu",
      title: "广东元数律师事务所",
      englishName: "Prime Partners",
      location: "广州 · 珠江新城",
      address: "广州市天河区珠江新城凯华国际中心 17 楼",
      excerpt: "专注互联网、人工智能与数字经济的综合性精品律所，立足华南、辐射大湾区、走向全国。",
      focus: ["互联网", "人工智能", "数字经济"],
      intro: [
        "广东元数律师事务所（以下简称“元数律师事务所”）于 2026 年 6 月由一群志同道合的律师发起设立，是一家专注于互联网、人工智能与数字经济领域的综合性精品律所。",
        "合伙人背景多元、能力互补——既有省、市律协互联网及知识产权相关专业委员会的主任、副主任，也有从头部互联网公司走出的前法务负责人。多元基因的碰撞，使“元数”自诞生起便兼具行业洞察、监管视野与商业思维。",
        "秉持“法之元、理之数”的理念，元数律师事务所已为字节跳动、华为等头部企业提供法律服务，立足华南，辐射大湾区，走向全国。",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_ZH_TW: GcsaLegalCounselSnapshot = {
  title: "GCSA 法律顧問",
  closeModal: "關閉",
  addressLabel: "辦公地址",
  focusLabel: "擅長領域",
  introTitle: "機構簡介",
  openIntro: "查看{name}簡介",
  firms: [
    {
      id: "leelee",
      title: "新加坡 李及李律師館",
      englishName: "Lee & Lee LLP",
      location: "新加坡 · 橋北路",
      address: "新加坡：25 North Bridge Road, Level 7, Singapore 179104（鄰近最高法院）",
      excerpt: "新加坡領先的全方位法律事務所 · 自1955年創立至今七十余年，以誠信、專業與戰略洞察力保護客戶利益。",
      focus: ["銀行與金融", "公司事務", "知識產權", "訴訟爭議", "房地產", "跨境業務"],
      intro: [
        "李及李律師館於1955年9月1日在新加坡創立，由李光耀先生、柯玉芝女士及李金耀先生共同創辦，現辦公地點位於橋北路25號7樓。本所植根新加坡七十余年，為個人、企業、金融機構及公共機構提供嚴謹、正直且具國際視野的法律服務。",
        "本所由創始人奠定的職業操守傳統延續至今，將深厚的普通法實務與超過七十年的庭審及交易經驗融為一體，以英、中等語言為跨境客戶提供服務，秉承「立足歷史經驗，順應未來趨勢」的理念，為現代商業與家族客戶帶來經得起時間考驗的法律解決方案。",
        "業務涵蓋銀行與金融、公司與併購、知識產權、訴訟與爭議解決、房地產與物業及各類專項法律領域。如今本所已發展為擁有逾180名律師、律師助理及支持人員的全方位律師行，並曾與國際律師事務所設立新加坡歷時最久的聯合法律合夥，服務三代以上的家族客戶及眾多上市公司與跨國集團。",
      ],
      disclaimer: "上述常年法律顧問關係並不構成 Lee & Lee LLP 對 GCSA 的業務、活動、運營、產品或服務作出的任何背書、支持、批准、核實、保證、擔保、陳述或承諾，亦不代表 Lee & Lee LLP 直接參與 GCSA 的任何業務、活動、運營、產品或服務。"
    },
    {
      id: "fn",
      title: "葡萄牙&澳門   弗朗西斯科·尼古勞博士律師事務所",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "里斯本 · 澳門",
      address: "里斯本：Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal；澳門：1/F, No. 194 Xinma Road, Macau",
      excerpt: "里斯本與澳門全方位法律事務所 · 橫跨葡萄牙與澳門三十餘年的卓越法律實踐。",
      focus: ["民法", "刑法", "跨境訴訟"],
      intro: [
        "弗朗西斯科·尼古勞博士律師事務所在里斯本與澳門設有辦公室，植根於橫跨葡萄牙與澳門三十餘年的卓越法律實踐，為個人、企業、機構及公共部門提供嚴謹、正直且具國際視野的法律諮詢。",
        "在弗朗西斯科·尼古勞博士——法學博士、資深出庭律師——的帶領下，本所將深厚的法理學素養與三十六年的庭審經驗融為一體，以葡、英、法、中及拉丁五種語言提供服務，為現代跨境客戶帶來嚴謹的大陸法系傳統。",
        "業務涵蓋民法、刑法、勞動法、行政法、民事訴訟與憲法等領域，並在葡萄牙與澳門各級法院擁有超過三十六年的出庭經驗。",
      ]
    },
    {
      id: "pmt",
      title: "香港麥家榮律師行",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "香港 · 中環",
      address: "香港中環干諾道中 111 號永安中心 9 樓",
      excerpt: "扎根香港、放眼海外的綜合性律師行，提供訴訟與非訴訟全方位多元化法律服務。",
      focus: ["企業融資", "商業及民事訴訟", "內地委託公證人"],
      intro: [
        "香港麥家榮律師行是一家扎根於香港、放眼於海外且具規模的綜合性律師事務所，能夠提供訴訟和非訴訟全方位多元化服務。",
        "自成立以來，香港麥家榮律師行在企業融資、商業及民事訴訟、內地委託公證人服務等核心法律服務中業績卓著，並以務實的辦事態度、專業的法律知識和豐富的行業經驗，為各地客戶提供高效率及高質量的法律服務。",
        "2024 年，香港麥家榮律師行榮膺 MYTHFOCUS 所頒發亞洲最有價值品牌大獎中的「大中華年度最佳法律顧問服務」獎項，展現了其不僅為客戶提供超出預期的服務與優質體驗，更具備預判及克服商業問題、持續提升服務能力以及對行業與社會的不懈貢獻。",
        "在持續服務客戶的過程中，香港麥家榮律師行與相關監管部門及國內外眾多證券公司、投資銀行、會計師事務所、資產評估機構及其他律師事務所建立了良好的業務聯繫，並積累了與政府機關及監管部門溝通協作的豐富經驗，能夠發揮優勢、整合資源，為客戶提供優質高效的法律服務。",
      ]
    },
    {
      id: "yuanshu",
      title: "廣東元數律師事務所",
      englishName: "Prime Partners",
      location: "廣州 · 珠江新城",
      address: "廣州市天河區珠江新城凱華國際中心 17 樓",
      excerpt: "專注互聯網、人工智能與數字經濟的綜合性精品律所，立足華南、輻射大灣區、走向全國。",
      focus: ["互聯網", "人工智能", "數字經濟"],
      intro: [
        "廣東元數律師事務所（以下簡稱「元數律師事務所」）於 2026 年 6 月由一群志同道合的律師發起設立，是一家專注於互聯網、人工智能與數字經濟領域的綜合性精品律所。",
        "合夥人背景多元、能力互補——既有省、市律協互聯網及知識產權相關專業委員會的主任、副主任，也有從頭部互聯網公司走出的前法務負責人。多元基因的碰撞，使「元數」自誕生起便兼具行業洞察、監管視野與商業思維。",
        "秉持「法之元、理之數」的理念，元數律師事務所已為字節跳動、華為等頭部企業提供法律服務，立足華南，輻射大灣區，走向全國。",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_EN_US: GcsaLegalCounselSnapshot = {
  title: "GCSA Legal Counsel",
  closeModal: "Close",
  addressLabel: "Office",
  focusLabel: "Practice focus",
  introTitle: "About the firm",
  openIntro: "View introduction for {name}",
  firms: [
    {
      id: "leelee",
      title: "Singapore Lee & Lee LLP",
      englishName: "Lee & Lee LLP",
      location: "Singapore · North Bridge Road",
      address: "Singapore: 25 North Bridge Road, Level 7, Singapore 179104 (near the Supreme Court)",
      excerpt: "One of Singapore’s leading full-service law firms—more than seven decades since 1955, protecting clients’ interests with integrity, expertise, and strategic insight.",
      focus: ["Banking & Finance", "Corporate", "Intellectual Property", "Litigation & Disputes", "Real Estate", "Cross-Border Matters"],
      intro: [
        "Lee & Lee was founded in Singapore on 1 September 1955 by Mr Lee Kuan Yew, Mdm Kwa Geok Choo, and Mr Dennis Lee Kim Yew. Today the firm is headquartered on Level 7 at 25 North Bridge Road. Rooted in Singapore for more than seventy years, it provides rigorous, principled, and internationally minded legal services to individuals, businesses, financial institutions, and public bodies.",
        "The professional ethos established by its founders continues to guide the firm, blending deep common-law practice with more than seventy years of courtroom and transactional experience. Advising cross-border clients in English and Chinese, Lee & Lee follows the philosophy of “grounded in history, aligned with the future”—delivering legal solutions that stand the test of time for modern businesses and families.",
        "Practice areas span banking and finance, corporate and M&A, intellectual property, litigation and dispute resolution, real estate and property, and specialised legal fields. The firm has grown into a full-service practice of more than 180 lawyers, paralegals, and support staff. It established one of Singapore’s longest-standing joint legal ventures with an international firm and serves family clients across three generations as well as listed companies and multinational groups.",
      ],
      disclaimer: "The ongoing legal counsel relationship described above does not constitute any endorsement, support, approval, verification, warranty, guarantee, representation, or commitment by Lee & Lee LLP regarding GCSA’s business, activities, operations, products, or services, nor does it indicate that Lee & Lee LLP directly participates in any of GCSA’s business, activities, operations, products, or services."
    },
    {
      id: "fn",
      title: "Portugal & Macau Dr. Francisco Nicolau & Associados",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "Lisbon · Macau",
      address: "Lisbon: Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal; Macau: 1/F, No. 194 Xinma Road, Macau",
      excerpt: "A full-service firm with offices in Lisbon and Macau—more than three decades of distinguished advocacy across Portugal and Macau.",
      focus: ["Civil Law", "Criminal Law", "Cross-Border Litigation"],
      intro: [
        "Dr. Francisco Nicolau & Associados is a full-service law firm with offices in Lisbon and Macau, founded on more than three decades of distinguished legal practice across Portugal and Macau. The firm delivers rigorous, principled, and internationally minded counsel to individuals, enterprises, institutions, and public bodies.",
        "Under the leadership of Dr. Francisco Nicolau—a Doctor of Legal Sciences and seasoned advocate—the firm unites deep doctrinal scholarship with 36 years of courtroom experience. We advise in Portuguese, English, French, Chinese, and Latin, bringing a rigorous civil-law tradition to the needs of a modern, cross-border clientele.",
        "Practice areas span civil law, criminal law, labour law, administrative law, civil procedure, and constitutional law, with more than 36 years of forensic practice before the full hierarchy of courts in Portugal and Macau.",
      ]
    },
    {
      id: "pmt",
      title: "Patrick Mak & Tse Solicitors",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "Hong Kong · Central",
      address: "9/F, Wing On Centre, 111 Connaught Road Central, Hong Kong",
      excerpt: "A Hong Kong–rooted, internationally minded full-service firm offering litigation and non-litigation counsel.",
      focus: ["Corporate Finance", "Commercial & Civil Litigation", "China-Appointed Attesting"],
      intro: [
        "Patrick Mak & Tse Solicitors is a full-service Hong Kong law firm with an international outlook, providing a broad range of litigation and non-litigation services.",
        "Since its founding, the firm has built a strong track record in corporate finance, commercial and civil litigation, and China-appointed attesting officer services—serving clients with a practical approach, specialist legal knowledge, and deep industry experience.",
        "In 2024, Patrick Mak & Tse was named \"Greater China Best Legal Counsel Service of the Year\" in the MYTHFOCUS Asia Most Valuable Brand Awards—reflecting not only service that exceeds client expectations, but also the firm's ability to anticipate and overcome commercial challenges, continually raise service quality, and contribute to the profession and society.",
        "Through ongoing client work, the firm has built strong relationships with regulators and with securities firms, investment banks, accounting firms, valuation firms, and peer law firms at home and abroad. Drawing on that network and its experience working with government and regulatory bodies, it coordinates resources to deliver efficient, high-quality counsel.",
      ]
    },
    {
      id: "yuanshu",
      title: "Prime Partners",
      englishName: "Prime Partners",
      location: "Guangzhou · Zhujiang New Town",
      address: "17/F, Kaihua International Center, Zhujiang New Town, Tianhe, Guangzhou",
      excerpt: "A boutique firm focused on internet, AI, and the digital economy—rooted in South China, serving the Greater Bay Area and clients nationwide.",
      focus: ["Internet", "Artificial Intelligence", "Digital Economy"],
      intro: [
        "Guangdong Prime Partners Law Firm (hereinafter referred to as \"Prime Partners\") was founded in June 2026 by a group of like-minded attorneys. It is a comprehensive boutique law firm specializing in the internet, artificial intelligence, and digital economy sectors.",
        "Its partners bring complementary backgrounds—including directors and deputy directors of provincial and municipal bar association committees on internet and intellectual property, as well as former legal heads from leading internet companies. That mix gives the firm industry insight, regulatory perspective, and commercial judgment from day one.",
        "Guided by the philosophy of \"The Origin of Law, The Logic of Numbers\", Prime Partners has provided legal services to leading enterprises including ByteDance and Huawei. Based in South China, it serves the Greater Bay Area and extends its reach nationwide.",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_TH_TH: GcsaLegalCounselSnapshot = {
  title: "ที่ปรึกษากฎหมาย",
  closeModal: "ปิดเมนู",
  addressLabel: "ช่องทางติดต่อ",
  focusLabel: "เกี่ยวกับเรา",
  introTitle: "เกี่ยวกับพันธมิตร",
  openIntro: "แนะนำพันธมิตร — {name}",
  firms: [
    {
      id: "leelee",
      title: "สิงคโปร์ Lee & Lee LLP",
      englishName: "Lee & Lee LLP",
      location: "สิงคโปร์ · North Bridge Road",
      address: "สิงคโปร์: 25 North Bridge Road, Level 7, Singapore 179104 (ใกล้ศาลฎีกา)",
      excerpt: "สำนักงานกฎหมายครบวงจรชั้นนำของสิงคโปร์ · ก่อตั้งตั้งแต่ปี 1955 มากว่าเจ็ดทศวรรษ ปกป้องผลประโยชน์ของลูกค้าด้วยความซื่อสัตย์ ความเชี่ยวชาญ และวิสัยทัศน์เชิงกลยุทธ์",
      focus: ["ธนาคารและการเงิน", "กิจการบริษัท", "ทรัพย์สินทางปัญญา", "คดีความและข้อพิพาท", "อสังหาริมทรัพย์", "ธุรกิจข้ามพรมแดน"],
      intro: [
        "Lee & Lee ก่อตั้งขึ้นที่สิงคโปร์เมื่อวันที่ 1 กันยายน 1955 โดยนาย Lee Kuan Yew นาง Kwa Geok Choo และนาย Dennis Lee Kim Yew ปัจจุบันสำนักงานตั้งอยู่ชั้น 7 เลขที่ 25 North Bridge Road สำนักงานหยั่งรากในสิงคโปร์มากว่าเจ็ดสิบปี ให้บริการกฎหมายที่รอบคอบ ซื่อตรง และมีมุมมองระหว่างประเทศแก่บุคคล ธุรกิจ สถาบันการเงิน และหน่วยงานสาธารณะ",
        "จรรยาบรรณวิชาชีพที่ผู้ก่อตั้งวางไว้สืบทอดมาจนถึงปัจจุบัน โดยผสานการปฏิบัติตามคอมมอนลอว์ที่ลึกซึ้งกับประสบการณ์ในศาลและธุรกรรมกว่าเจ็ดสิบปี ให้บริการลูกค้าข้ามพรมแดนเป็นภาษาอังกฤษและภาษาจีน ยึดแนวคิด «ยืนบนประสบการณ์ทางประวัติศาสตร์ และสอดรับแนวโน้มอนาคต» เพื่อนำเสนอทางออกทางกฎหมายที่ทนทานต่อกาลเวลาแก่ธุรกิจสมัยใหม่และลูกค้าที่เป็นครอบครัว",
        "ขอบเขตงานครอบคลุมธนาคารและการเงิน บริษัทและการควบรวม ทรัพย์สินทางปัญญา คดีความและการระงับข้อพิพาท อสังหาริมทรัพย์และทรัพย์สิน ตลอดจนสาขากฎหมายเฉพาะทาง ปัจจุบันสำนักงานเติบโตเป็นสำนักงานครบวงจรที่มีทนาย ผู้ช่วยทนาย และเจ้าหน้าที่สนับสนุนมากกว่า 180 คน และเคยจัดตั้งห้างหุ้นส่วนทางกฎหมายร่วมกับสำนักงานกฎหมายระหว่างประเทศซึ่งเป็นหนึ่งในความร่วมมือที่ยาวนานที่สุดในสิงคโปร์ ให้บริการลูกค้าครอบครัวมากกว่าสามรุ่น รวมถึงบริษัทจดทะเบียนและกลุ่มข้ามชาติจำนวนมาก",
      ],
      disclaimer: "ความสัมพันธ์ที่ปรึกษากฎหมายประจำปีข้างต้นไม่ถือเป็นการรับรอง สนับสนุน อนุมัติ ตรวจสอบ รับประกัน ค้ำประกัน แถลง หรือให้คำมั่นใด ๆ ของ Lee & Lee LLP ต่องาน กิจกรรม การดำเนินงาน ผลิตภัณฑ์ หรือบริการของ GCSA และไม่หมายความว่า Lee & Lee LLP มีส่วนร่วมโดยตรงในงาน กิจกรรม การดำเนินงาน ผลิตภัณฑ์ หรือบริการใดของ GCSA"
    },
    {
      id: "fn",
      title: "โปรตุเกสและมาเก๊า Dr. Francisco Nicolau & Associados",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "ลิสบอน · มาเก๊า",
      address: "ลิสบอน: Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal; มาเก๊า: 1/F, No. 194 Xinma Road, Macau",
      excerpt: "สำนักงานกฎหมายครบวงจรในลิสบอนและมาเก๊า · การปฏิบัติทางกฎหมายที่เป็นเลิศกว่าสามทศวรรษในโปรตุเกสและมาเก๊า",
      focus: ["กฎหมายแพ่ง", "กฎหมายอาญา", "คดีความข้ามพรมแดน"],
      intro: [
        "Dr. Francisco Nicolau & Associados มีสำนักงานในลิสบอนและมาเก๊า ตั้งอยู่บนการปฏิบัติทางกฎหมายที่เป็นเลิศกว่าสามทศวรรษในโปรตุเกสและมาเก๊า ให้คำปรึกษากฎหมายที่รอบคอบ ซื่อตรง และมีมุมมองระหว่างประเทศแก่บุคคล ธุรกิจ สถาบัน และภาครัฐ",
        "ภายใต้การนำของ Dr. Francisco Nicolau ซึ่งเป็นดุษฎีบัณฑิตด้านนิติศาสตร์และทนายความว่าความที่มีประสบการณ์ สำนักงานผสานความรู้ทางนิติศาสตร์ที่ลึกซึ้งกับประสบการณ์ในศาล 36 ปี ให้บริการเป็นภาษาโปรตุเกส อังกฤษ ฝรั่งเศส จีน และละติน เพื่อนำประเพณีกฎหมายภาคพื้นทวีปที่รอบคอบมาสู่ลูกค้าข้ามพรมแดนในยุคปัจจุบัน",
        "ขอบเขตงานครอบคลุมกฎหมายแพ่ง อาญา แรงงาน ปกครอง วิธีพิจารณาความแพ่ง และรัฐธรรมนูญ และมีประสบการณ์ว่าความในศาลทุกระดับของโปรตุเกสและมาเก๊ามากกว่า 36 ปี",
      ]
    },
    {
      id: "pmt",
      title: "Patrick Mak & Tse Solicitors",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "ฮ่องกง · เซ็นทรัล",
      address: "ชั้น 9 Wing On Centre, 111 Connaught Road Central, ฮ่องกง",
      excerpt: "สำนักงานกฎหมายครบวงจรที่มีรากในฮ่องกงและมองสู่ต่างประเทศ ให้บริการกฎหมายที่หลากหลายทั้งคดีความและงานที่ไม่ใช่คดีความ",
      focus: ["การเงินองค์กร", "คดีพาณิชย์และแพ่ง", "โนตารีที่ได้รับแต่งตั้งจากจีนแผ่นดินใหญ่"],
      intro: [
        "Patrick Mak & Tse Solicitors เป็นสำนักงานกฎหมายครบวงจรขนาดใหญ่ที่มีรากในฮ่องกงและมองสู่ต่างประเทศ สามารถให้บริการที่หลากหลายทั้งคดีความและงานที่ไม่ใช่คดีความ",
        "นับตั้งแต่ก่อตั้ง Patrick Mak & Tse มีผลงานโดดเด่นในบริการกฎหมายหลักด้านการเงินองค์กร คดีพาณิชย์และแพ่ง และบริการโนตารีที่ได้รับแต่งตั้งจากจีนแผ่นดินใหญ่ และให้บริการกฎหมายที่มีประสิทธิภาพและคุณภาพสูงแก่ลูกค้าในหลายพื้นที่ด้วยท่าทีที่ปฏิบัติได้จริง ความรู้ทางกฎหมายเฉพาะทาง และประสบการณ์ในอุตสาหกรรม",
        "ในปี 2024 Patrick Mak & Tse ได้รับรางวัล «บริการที่ปรึกษากฎหมายยอดเยี่ยมแห่งปีของจีนแผ่นดินใหญ่ ฮ่องกง และไต้หวัน» ในงาน MYTHFOCUS Asia Most Valuable Brand Awards ซึ่งสะท้อนว่าสำนักงานไม่เพียงให้บริการและประสบการณ์ที่เกินความคาดหมายของลูกค้า แต่ยังคาดการณ์และเอาชนะปัญหาทางธุรกิจ ยกระดับบริการอย่างต่อเนื่อง และมีส่วนร่วมต่อวิชาชีพและสังคมอย่างไม่ลดละ",
        "ในการให้บริการลูกค้าอย่างต่อเนื่อง สำนักงานได้สร้างความสัมพันธ์ทางธุรกิจที่ดีกับหน่วยงานกำกับดูแลที่เกี่ยวข้อง รวมถึงบริษัทหลักทรัพย์ ธนาคารเพื่อการลงทุน สำนักงานบัญชี สถาบันประเมินมูลค่า และสำนักงานกฎหมายอื่นทั้งในและต่างประเทศ และสั่งสมประสบการณ์ในการสื่อสารและทำงานร่วมกับหน่วยงานรัฐและหน่วยงานกำกับดูแล จึงสามารถใช้จุดแข็งและบูรณาการทรัพยากรเพื่อให้คำปรึกษาทางกฎหมายที่มีคุณภาพและมีประสิทธิภาพ",
      ]
    },
    {
      id: "yuanshu",
      title: "Guangdong Prime Partners",
      englishName: "Prime Partners",
      location: "กว่างโจว · Zhujiang New Town",
      address: "ชั้น 17 Kaihua International Center, Zhujiang New Town, Tianhe, กว่างโจว",
      excerpt: "สำนักงานกฎหมายบูติกครบวงจรที่มุ่งเน้นอินเทอร์เน็ต ปัญญาประดิษฐ์ และเศรษฐกิจดิจิทัล ตั้งหลักในจีนตอนใต้ ครอบคลุมเขตอ่าวใหญ่ และขยายสู่ทั่วประเทศ",
      focus: ["อินเทอร์เน็ต", "ปัญญาประดิษฐ์", "เศรษฐกิจดิจิทัล"],
      intro: [
        "Guangdong Prime Partners Law Firm (ต่อไปนี้เรียกว่า «Prime Partners») ก่อตั้งขึ้นในเดือนมิถุนายน 2026 โดยกลุ่มทนายความที่มีอุดมการณ์ร่วมกัน เป็นสำนักงานกฎหมายบูติกครบวงจรที่มุ่งเน้นอินเทอร์เน็ต ปัญญาประดิษฐ์ และเศรษฐกิจดิจิทัล",
        "หุ้นส่วนมีภูมิหลังหลากหลายและทักษะที่เสริมกัน ทั้งประธานและรองประธานคณะกรรมการวิชาชีพด้านอินเทอร์เน็ตและทรัพย์สินทางปัญญาของสมาคมทนายระดับมณฑลและเมือง และอดีตหัวหน้าฝ่ายกฎหมายจากบริษัทอินเทอร์เน็ตชั้นนำ การผสานนี้ทำให้ Prime Partners มีทั้งความเข้าใจอุตสาหกรรม มุมมองด้านการกำกับดูแล และความคิดเชิงธุรกิจตั้งแต่เริ่มต้น",
        "ยึดแนวคิด «ต้นธารของกฎหมาย ตรรกะของจำนวน» Prime Partners ได้ให้บริการกฎหมายแก่กิจการชั้นนำอย่าง ByteDance และ Huawei โดยตั้งหลักในจีนตอนใต้ ครอบคลุมเขตอ่าวใหญ่ และขยายสู่ทั่วประเทศ",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_ID_ID: GcsaLegalCounselSnapshot = {
  title: "Penasihat hukum",
  closeModal: "Tutup menu",
  addressLabel: "Cara menghubungi",
  focusLabel: "Tentang kami",
  introTitle: "Tentang Aliansi",
  openIntro: "Pengenalan Aliansi — {name}",
  firms: [
    {
      id: "leelee",
      title: "Singapura Lee & Lee LLP",
      englishName: "Lee & Lee LLP",
      location: "Singapura · North Bridge Road",
      address: "Singapura: 25 North Bridge Road, Level 7, Singapore 179104 (dekat Mahkamah Agung)",
      excerpt: "Firma hukum layanan penuh terkemuka di Singapura · lebih dari tujuh dekade sejak berdiri pada 1955, melindungi kepentingan klien dengan integritas, keahlian, dan wawasan strategis.",
      focus: ["Perbankan dan keuangan", "Urusan perusahaan", "Kekayaan intelektual", "Litigasi dan sengketa", "Properti", "Urusan lintas batas"],
      intro: [
        "Lee & Lee didirikan di Singapura pada 1 September 1955 oleh Bapak Lee Kuan Yew, Ibu Kwa Geok Choo, dan Bapak Dennis Lee Kim Yew. Kini kantornya berada di Lantai 7, 25 North Bridge Road. Berakar di Singapura selama lebih dari tujuh puluh tahun, firma ini memberikan layanan hukum yang cermat, berintegritas, dan berwawasan internasional kepada perorangan, perusahaan, lembaga keuangan, dan badan publik.",
        "Etika profesi yang diletakkan para pendiri berlanjut hingga kini, memadukan praktik common law yang mendalam dengan lebih dari tujuh puluh tahun pengalaman persidangan dan transaksi. Firma melayani klien lintas batas dalam bahasa Inggris dan Tionghoa, dengan filosofi «berpijak pada pengalaman sejarah, selaras dengan tren masa depan», menghadirkan solusi hukum yang tahan waktu bagi bisnis modern dan klien keluarga.",
        "Bidang praktik mencakup perbankan dan keuangan, perusahaan dan merger-akuisisi, kekayaan intelektual, litigasi dan penyelesaian sengketa, properti, serta berbagai bidang hukum khusus. Kini firma telah berkembang menjadi praktik layanan penuh dengan lebih dari 180 pengacara, paralegal, dan staf pendukung, serta pernah membentuk kemitraan hukum bersama dengan firma internasional yang termasuk yang paling lama di Singapura, melayani klien keluarga lintas lebih dari tiga generasi serta banyak perusahaan tercatat dan grup multinasional.",
      ],
      disclaimer: "Hubungan penasihat hukum tetap yang diuraikan di atas tidak merupakan dukungan, persetujuan, verifikasi, jaminan, garansi, pernyataan, atau komitmen apa pun dari Lee & Lee LLP atas bisnis, kegiatan, operasi, produk, atau layanan GCSA, dan tidak berarti Lee & Lee LLP terlibat langsung dalam bisnis, kegiatan, operasi, produk, atau layanan GCSA mana pun."
    },
    {
      id: "fn",
      title: "Portugal & Makau Dr. Francisco Nicolau & Associados",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "Lisbon · Makau",
      address: "Lisbon: Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal; Makau: 1/F, No. 194 Xinma Road, Macau",
      excerpt: "Firma layanan penuh di Lisbon dan Makau · lebih dari tiga dekade praktik hukum yang terkemuka di Portugal dan Makau.",
      focus: ["Hukum perdata", "Hukum pidana", "Litigasi lintas batas"],
      intro: [
        "Dr. Francisco Nicolau & Associados memiliki kantor di Lisbon dan Makau, berakar pada praktik hukum terkemuka selama lebih dari tiga dekade di Portugal dan Makau, memberikan nasihat hukum yang cermat, berintegritas, dan berwawasan internasional kepada perorangan, perusahaan, lembaga, dan sektor publik.",
        "Di bawah kepemimpinan Dr. Francisco Nicolau—doktor ilmu hukum dan advokat berpengalaman—firma memadukan kedalaman keilmuan hukum dengan 36 tahun pengalaman persidangan, melayani dalam bahasa Portugis, Inggris, Prancis, Tionghoa, dan Latin, membawa tradisi civil law yang ketat bagi klien lintas batas masa kini.",
        "Bidang praktik mencakup hukum perdata, pidana, ketenagakerjaan, administrasi, acara perdata, dan konstitusi, dengan lebih dari 36 tahun pengalaman beracara di seluruh tingkat pengadilan Portugal dan Makau.",
      ]
    },
    {
      id: "pmt",
      title: "Patrick Mak & Tse Solicitors",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "Hong Kong · Central",
      address: "Lantai 9, Wing On Centre, 111 Connaught Road Central, Hong Kong",
      excerpt: "Firma layanan penuh yang berakar di Hong Kong dan berpandangan internasional, menyediakan layanan hukum yang beragam baik litigasi maupun nonlitigasi.",
      focus: ["Pembiayaan perusahaan", "Litigasi komersial dan perdata", "Notaris yang ditunjuk Tiongkok daratan"],
      intro: [
        "Patrick Mak & Tse Solicitors adalah firma hukum layanan penuh berskala yang berakar di Hong Kong dan berpandangan ke luar negeri, mampu menyediakan layanan yang beragam baik litigasi maupun nonlitigasi.",
        "Sejak berdiri, firma ini mencatat kinerja kuat dalam pembiayaan perusahaan, litigasi komersial dan perdata, serta layanan notaris yang ditunjuk Tiongkok daratan, dan melayani klien di berbagai tempat dengan sikap praktis, pengetahuan hukum spesialis, serta pengalaman industri yang dalam.",
        "Pada 2024, Patrick Mak & Tse meraih penghargaan «Layanan Penasihat Hukum Terbaik Tahun Ini di Tiongkok Raya» dalam MYTHFOCUS Asia Most Valuable Brand Awards. Penghargaan itu mencerminkan layanan dan pengalaman yang melampaui harapan klien, serta kemampuan mengantisipasi dan mengatasi masalah komersial, terus meningkatkan mutu layanan, dan berkontribusi pada profesi serta masyarakat.",
        "Melalui layanan klien yang berkelanjutan, firma membangun hubungan baik dengan regulator terkait serta banyak perusahaan sekuritas, bank investasi, kantor akuntan, lembaga penilai, dan firma hukum lain di dalam maupun luar negeri. Dengan pengalaman berkoordinasi bersama lembaga pemerintah dan regulator, firma mengoordinasikan sumber daya untuk memberikan nasihat hukum yang bermutu dan efisien.",
      ]
    },
    {
      id: "yuanshu",
      title: "Guangdong Prime Partners",
      englishName: "Prime Partners",
      location: "Guangzhou · Zhujiang New Town",
      address: "Lantai 17, Kaihua International Center, Zhujiang New Town, Tianhe, Guangzhou",
      excerpt: "Firma butik komprehensif yang berfokus pada internet, kecerdasan buatan, dan ekonomi digital—berakar di Tiongkok Selatan, menjangkau Kawasan Teluk Raya, dan melayani seluruh negeri.",
      focus: ["Internet", "Kecerdasan buatan", "Ekonomi digital"],
      intro: [
        "Guangdong Prime Partners Law Firm (selanjutnya disebut «Prime Partners») didirikan pada Juni 2026 oleh sekelompok pengacara yang sepaham. Ini adalah firma butik komprehensif yang berfokus pada internet, kecerdasan buatan, dan ekonomi digital.",
        "Para mitra memiliki latar yang beragam dan saling melengkapi—termasuk ketua dan wakil ketua komite profesional asosiasi advokat provinsi dan kota di bidang internet serta kekayaan intelektual, juga mantan kepala hukum dari perusahaan internet terkemuka. Perpaduan itu memberi Prime Partners wawasan industri, perspektif regulasi, dan pemikiran bisnis sejak awal.",
        "Dengan filosofi «Asal Hukum, Logika Angka», Prime Partners telah memberikan layanan hukum kepada perusahaan terkemuka termasuk ByteDance dan Huawei. Berbasis di Tiongkok Selatan, firma ini menjangkau Kawasan Teluk Raya dan meluas ke seluruh negeri.",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_KO_KR: GcsaLegalCounselSnapshot = {
  title: "법률 자문",
  closeModal: "메뉴 닫기",
  addressLabel: "연락처",
  focusLabel: "소개",
  introTitle: "연합 소개",
  openIntro: "연합 개요 — {name}",
  firms: [
    {
      id: "leelee",
      title: "싱가포르 Lee & Lee LLP",
      englishName: "Lee & Lee LLP",
      location: "싱가포르 · North Bridge Road",
      address: "싱가포르: 25 North Bridge Road, Level 7, Singapore 179104 (대법원 인근)",
      excerpt: "싱가포르를 대표하는 종합 법률사무소 · 1955년 설립 이후 70여 년 동안 성실, 전문성, 전략적 통찰로 고객의 이익을 지켜 왔습니다.",
      focus: ["은행 및 금융", "기업 업무", "지식재산", "소송 및 분쟁", "부동산", "국경 간 업무"],
      intro: [
        "Lee & Lee는 1955년 9월 1일 싱가포르에서 Lee Kuan Yew 씨, Kwa Geok Choo 여사, Dennis Lee Kim Yew 씨가 함께 설립했으며, 현재 사무소는 North Bridge Road 25번지 7층에 있습니다. 싱가포르에 70여 년 뿌리를 두고 개인, 기업, 금융기관, 공공기관에 엄정하고 정직하며 국제적 시각을 갖춘 법률 서비스를 제공합니다.",
        "창립자가 세운 직업 윤리는 지금까지 이어지며, 깊은 보통법 실무와 70년 이상의 법정·거래 경험을 하나로 묶습니다. 영어와 중국어 등으로 국경 간 고객에게 서비스하며, “역사적 경험에 서고 미래의 흐름에 맞춘다”는 이념으로 현대 기업과 가족 고객에게 시간을 견디는 법률 해법을 제공합니다.",
        "업무는 은행·금융, 기업과 M&A, 지식재산, 소송과 분쟁 해결, 부동산, 그리고 각종 전문 법률 분야를 아우릅니다. 지금은 변호사, 변호사 보조, 지원 인력 180명 이상이 있는 종합 로펌으로 성장했으며, 국제 로펌과 싱가포르에서 가장 오래 지속된 공동 법률 파트너십 중 하나를 설립해 3대 이상의 가족 고객과 다수의 상장사·다국적 그룹에 서비스를 제공합니다.",
      ],
      disclaimer: "위에 설명한 상임 법률 고문 관계는 Lee & Lee LLP가 GCSA의 사업, 활동, 운영, 제품 또는 서비스에 대해 보증, 지지, 승인, 확인, 보장, 담보, 진술 또는 약속을 한 것이 아니며, Lee & Lee LLP가 GCSA의 사업, 활동, 운영, 제품 또는 서비스에 직접 참여한다는 뜻도 아닙니다."
    },
    {
      id: "fn",
      title: "포르투갈 및 마카오 Dr. Francisco Nicolau & Associados",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "리스본 · 마카오",
      address: "리스본: Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal; 마카오: 1/F, No. 194 Xinma Road, Macau",
      excerpt: "리스본과 마카오의 종합 법률사무소 · 포르투갈과 마카오를 아우르는 30여 년의 탁월한 법률 실무.",
      focus: ["민법", "형법", "국경 간 소송"],
      intro: [
        "Dr. Francisco Nicolau & Associados는 리스본과 마카오에 사무소를 두고, 포르투갈과 마카오를 아우르는 30여 년의 탁월한 법률 실무에 뿌리를 두며, 개인, 기업, 기관, 공공 부문에 엄정하고 정직하며 국제적 시각의 법률 자문을 제공합니다.",
        "법학 박사이자 노련한 법정 변호사 Dr. Francisco Nicolau의 리더십 아래, 사무소는 깊은 법학 소양과 36년의 법정 경험을 하나로 묶고 포르투갈어, 영어, 프랑스어, 중국어, 라틴어로 서비스하며, 현대의 국경 간 고객에게 엄정한 대륙법 전통을 제공합니다.",
        "업무는 민법, 형법, 노동법, 행정법, 민사소송, 헌법을 아우르며, 포르투갈과 마카오 각급 법원에서 36년 이상의 법정 경험을 갖추고 있습니다.",
      ]
    },
    {
      id: "pmt",
      title: "Patrick Mak & Tse Solicitors",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "홍콩 · 센트럴",
      address: "홍콩 중환 Connaught Road Central 111 Wing On Centre 9층",
      excerpt: "홍콩에 뿌리를 두고 해외를 바라보는 종합 로펌으로, 소송과 비소송 전반의 다양한 법률 서비스를 제공합니다.",
      focus: ["기업 금융", "상사 및 민사 소송", "중국 본토 위탁 공증인"],
      intro: [
        "Patrick Mak & Tse Solicitors는 홍콩에 뿌리를 두고 해외를 바라보는 규모 있는 종합 법률사무소로, 소송과 비소송 전반의 다양한 서비스를 제공합니다.",
        "설립 이래 기업 금융, 상사·민사 소송, 중국 본토 위탁 공증 등 핵심 법률 서비스에서 뚜렷한 실적을 쌓았으며, 실용적인 태도, 전문 법률 지식, 풍부한 업계 경험으로 각지 고객에게 효율적이고 수준 높은 법률 서비스를 제공합니다.",
        "2024년 Patrick Mak & Tse는 MYTHFOCUS 아시아 최고 가치 브랜드 시상식에서 «중화권 올해의 최우수 법률 고문 서비스»를 수상했습니다. 이는 고객 기대를 넘는 서비스와 경험뿐 아니라, 사업 문제를 예측하고 극복하며 서비스 역량을 계속 높이고 업계와 사회에 기여하는 모습을 보여 줍니다.",
        "고객 서비스를 이어 오며 관련 규제 기관과 국내외 증권사, 투자은행, 회계법인, 자산평가기관, 다른 법률사무소와 좋은 업무 관계를 쌓았고, 정부 기관 및 규제 기관과의 소통·협력 경험도 축적했습니다. 그 강점을 살려 자원을 모아 고객에게 수준 높고 효율적인 법률 서비스를 제공합니다.",
      ]
    },
    {
      id: "yuanshu",
      title: "광둥 Prime Partners",
      englishName: "Prime Partners",
      location: "광저우 · 주장신청",
      address: "광저우 톈허구 주장신청 Kaihua International Center 17층",
      excerpt: "인터넷, 인공지능, 디지털 경제에 집중하는 종합 부티크 로펌으로, 화남에 뿌리를 두고 그레이터베이와 전국으로 서비스합니다.",
      focus: ["인터넷", "인공지능", "디지털 경제"],
      intro: [
        "광둥 Prime Partners 법률사무소(이하 «Prime Partners»)는 2026년 6월 뜻을 같이하는 변호사들이 설립한, 인터넷·인공지능·디지털 경제에 집중하는 종합 부티크 로펌입니다.",
        "파트너의 배경은 다양하고 역량이 보완됩니다. 성·시 변호사협회 인터넷 및 지식재산 전문위원회의 주임·부주임과, 선도 인터넷 기업의 전 법무 책임자가 함께합니다. 그 조합 덕분에 Prime Partners는 출범 때부터 산업 통찰, 규제 시각, 사업 사고를 함께 갖춥니다.",
        "«법의 근원, 수의 논리»라는 이념으로 Prime Partners는 ByteDance, Huawei 등 선도 기업에 법률 서비스를 제공했으며, 화남을 기반으로 그레이터베이와 전국으로 뻗어 갑니다.",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_JA_JP: GcsaLegalCounselSnapshot = {
  title: "法律顧問",
  closeModal: "メニューを閉じる",
  addressLabel: "お問い合わせ",
  focusLabel: "私たちについて",
  introTitle: "アライアンスについて",
  openIntro: "アライアンス概要 — {name}",
  firms: [
    {
      id: "leelee",
      title: "シンガポール・リー＆リーLLP",
      englishName: "リー＆リーLLP",
      location: "シンガポール・ノースブリッジ・ロード",
      address: "シンガポール：25 North Bridge Road, Level 7, Singapore 179104（最高裁判所近隣）",
      excerpt: "シンガポールを代表するフルサービス法律事務所の一つです。1955年以降70年以上にわたり、誠実さ、専門知識、戦略的洞察を基に、クライアントの利益を保護してきました。",
      focus: ["銀行・金融", "企業用", "知的財産", "訴訟および紛争", "不動産", "国境を越えた課題"],
      intro: [
        "Lee & Leeは1955年9月1日、リー・クアン・ユウ氏、クア・ゲオク・チュ氏、およびデニス・リー・キム・ユウ氏によってシンガポールで設立されました。本社は現在、ノース・ブリッジ・ロード25番地の7階に置かれており、70年以上にわたりシンガポールを拠点として、個人、企業、金融機関および公共機関に対して、厳密かつ原則に基づき、国際的視野を持つ法的サービスを提供しています。",
        "創設者たちが築いた専門職の倫理観は、今なおリー・アンド・リーを導く基盤となっており、深い共通法の実務経験と、70年以上にわたる法廷および取引に関する豊富な知見を融合させています。リー・アンド・リーは、英語および中国語で国境を越えたクライアントに助言を提供しており、「歴史に根ざし、未来と調和する」という理念に基づき、現代の企業および家族が長期間にわたり通用する法的解決策を提供しています。",
        "当法律事務所は、銀行・金融、企業およびM&A、知的財産、訴訟および紛争解決、不動産・物件、ならびに専門的な法務分野など、多岐にわたる分野で業務を展開しています。現在では、180名以上の弁護士、法務支援スタッフおよびサポートスタッフから構成される包括的な法務サービス体制を確立しています。また、国際的な法律事務所とシンガポールで最も長期間にわたり継続している共同法務事業を一つ設立し、3世代にわたる家族のクライアントに加え、上場企業および多国籍企業グループに対しても支援を提供しています。",
      ],
      disclaimer: "上記に述べた法的助言に関する関係は、Lee & Lee LLPがGCSAの事業、活動、運営、製品またはサービスについて、何らかの承認、支援、承認、検証、保証、担保、表明または約束を有するものではなく、またLee & Lee LLPがGCSAの事業、活動、運営、製品またはサービスに直接関与していることを示すものでもない。"
    },
    {
      id: "fn",
      title: "ポルトガル・マカオ：フランシスコ・ニコラウ・アンド・アソシエイドス博士",
      englishName: "フランシスコ・ニコラウ博士および同僚事務所",
      location: "リスボン・マカオ",
      address: "リスボン：ルア・マヌエーラ・ポルト、10番地、2号棟、1500-422 リスボン、ポルトガル；マカオ：1階194番地、シンマ通り、マカオ",
      excerpt: "リスボンおよびマカオに事務所を構えるフルサービス型法律事務所であり、ポルトガルおよびマカオにおいて30年以上にわたり、卓越した法的支援を提供してきました。",
      focus: ["民法", "刑法", "国境を越えた訴訟"],
      intro: [
        "フランシスコ・ニコラウ・アンド・アソシエイドス法律事務所は、リスボンおよびマカオに事務所を構えるフルサービス型法律事務所であり、ポルトガルおよびマカオにおける30年以上にわたる優れた法的実務実績を基盤として設立されました。当事務所は、個人、企業、機関および公共機関に対し、厳密かつ原則に基づき、国際的視野を持つ法的助言を提供しています。",
        "法科学の博士号を有し、豊富な訴訟経験を持つフランシスコ・ニコラウ博士の指導の下、本法律事務所は、深い法理学的知見と36年にわたる法廷での実践経験を融合させています。当事務所はポルトガル語、英語、フランス語、中国語、ラテン語で助言を提供しており、厳密な大陸法系の伝統を、現代の越境を特徴とするクライアントのニーズに応えるべく活用しています。",
        "専門分野は民事法、刑事法、労働法、行政法、民事手続法、および憲法法に及び、ポルトガルおよびマカオの全段階の裁判所において36年以上にわたり法医学的実務に従事しています。",
      ]
    },
    {
      id: "pmt",
      title: "パトリック・マク＆ツェ法律事務所",
      englishName: "パトリック・マク＆ツェ法律事務所",
      location: "香港・セントラル",
      address: "香港・コンナート・ロード・セントラル 111番地、Wing On Centre 9階",
      excerpt: "香港を拠点とし、国際的視野を持つフルサービス法務事務所。訴訟および非訴訟に関する法的支援を提供しています。",
      focus: ["企業財務", "商事・民事訴訟", "中国側による認証"],
      intro: [
        "Patrick Mak & Tse Solicitorsは、国際的な視野を持つ香港の総合法律事務所であり、訴訟および非訴訟に関する幅広いサービスを提供しています。",
        "設立以来、本事務所は企業財務、商業・民事訴訟、および中国側から任命された検証役員のサービスにおいて、確固たる実績を築いており、実用的なアプローチ、専門的な法的知識、および豊富な業界経験を備え、クライアントに的確な支援を提供しています。",
        "2024年、Patrick Mak & Tseは「mythfocus Asia Most Valuable Brand Awards」において「Greater China Best Legal Counsel Service of the Year」に選出されました。これは、クライアントの期待を超えるサービスを提供していることに加え、同法律事務所が商業上の課題を的確に予測し克服する能力、サービス品質の継続的な向上、そして法律業界および社会への貢献を有していることを示しています。",
        "当法律事務所は、継続的なクライアント案件を通じて、国内外の規制当局および証券会社、投資銀行、会計事務所、企業評価会社、同業他社の法律事務所との強固な関係を築いてきました。こうしたネットワークおよび政府・規制機関との協働経験を活かし、必要なリソースを統合して、効率的かつ高品質な法的助言を提供しています。",
      ]
    },
    {
      id: "yuanshu",
      title: "プライム・パートナーズ",
      englishName: "プライム・パートナーズ",
      location: "広州・珠江新城",
      address: "女性・17歳、広州・天河区・珠江新城・カイフア・インターナショナル・センター",
      excerpt: "インターネット、AI、およびデジタル経済に特化したブティック型企業です。中国南部を拠点としており、大規模港湾地域および全国のクライアントにサービスを提供しています。",
      focus: ["インターネット", "人工知能", "デジタル経済"],
      intro: [
        "広東省プライム・パートナーズ法律事務所（以下「プライム・パートナーズ」という）は、共通の理念を持つ弁護士グループによって2026年6月に設立されました。本事務所は、インターネット、人工知能、およびデジタル経済分野に特化した包括的なブティック法律事務所です。",
        "本法律事務所のパートナーには、省・市レベルの弁護士会におけるインターネットおよび知的財産に関する委員会を率いる理事および副理事、ならびに主要なインターネット企業で法務部門の責任者を務めた経歴を持つ人材が参画しています。こうした構成により、本事務所は創業当初から業界に関する深い知見、規制に関する視点、および商業的判断力を備えています。",
        "「法の起源、数値の論理」という理念に基づき、Prime PartnersはByteDanceやHuaweiなど主要企業に対して法的支援を提供してきました。南中国に本拠を置き、大規模港湾地域を主な対象地域として活動するとともに、そのサービス網を全国へと拡大しています。",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_VI_VN: GcsaLegalCounselSnapshot = {
  title: "Cố vấn pháp lý",
  closeModal: "Đóng menu",
  addressLabel: "Cách liên hệ",
  focusLabel: "Về chúng tôi",
  introTitle: "Về liên minh",
  openIntro: "Giới thiệu liên minh — {name}",
  firms: [
    {
      id: "leelee",
      title: "Singapore Lee & Lee LLP",
      englishName: "Lee & Lee LLP",
      location: "Singapore · North Bridge Road",
      address: "Singapore: 25 North Bridge Road, Level 7, Singapore 179104 (gần Tối cao pháp viện)",
      excerpt: "Một trong những hãng luật toàn diện hàng đầu Singapore · hơn bảy thập kỷ kể từ năm 1955, bảo vệ lợi ích khách hàng bằng sự chính trực, chuyên môn và tầm nhìn chiến lược.",
      focus: ["Ngân hàng và tài chính", "Công việc công ty", "Sở hữu trí tuệ", "Tố tụng và tranh chấp", "Bất động sản", "Hoạt động xuyên biên giới"],
      intro: [
        "Lee & Lee được thành lập tại Singapore vào ngày 1 tháng 9 năm 1955 bởi ông Lee Kuan Yew, bà Kwa Geok Choo và ông Dennis Lee Kim Yew. Văn phòng hiện nay ở tầng 7, số 25 North Bridge Road. Gắn bó với Singapore hơn bảy mươi năm, hãng cung cấp dịch vụ pháp lý chặt chẽ, chính trực và có tầm nhìn quốc tế cho cá nhân, doanh nghiệp, tổ chức tài chính và cơ quan công.",
        "Chuẩn mực nghề nghiệp do các nhà sáng lập đặt nền tiếp tục đến hôm nay, kết hợp thực tiễn common law sâu sắc với hơn bảy mươi năm kinh nghiệm tranh tụng và giao dịch. Hãng phục vụ khách hàng xuyên biên giới bằng tiếng Anh và tiếng Trung, theo phương châm «đứng trên kinh nghiệm lịch sử, thuận theo xu thế tương lai», mang đến giải pháp pháp lý chịu được thử thách thời gian cho doanh nghiệp hiện đại và khách hàng gia đình.",
        "Lĩnh vực gồm ngân hàng và tài chính, công ty và M&A, sở hữu trí tuệ, tố tụng và giải quyết tranh chấp, bất động sản và tài sản, cùng các lĩnh vực pháp lý chuyên biệt. Nay hãng đã phát triển thành văn phòng toàn diện với hơn 180 luật sư, trợ lý luật sư và nhân viên hỗ trợ, từng thành lập liên doanh pháp lý với hãng quốc tế thuộc số lâu đời nhất tại Singapore, phục vụ khách hàng gia đình qua hơn ba thế hệ cùng nhiều công ty niêm yết và tập đoàn đa quốc gia.",
      ],
      disclaimer: "Quan hệ cố vấn pháp lý thường niên nêu trên không cấu thành bất kỳ sự bảo chứng, ủng hộ, phê duyệt, xác minh, bảo đảm, bảo lãnh, tuyên bố hoặc cam kết nào của Lee & Lee LLP đối với hoạt động, sự kiện, vận hành, sản phẩm hoặc dịch vụ của GCSA, và cũng không có nghĩa Lee & Lee LLP trực tiếp tham gia bất kỳ hoạt động, sự kiện, vận hành, sản phẩm hoặc dịch vụ nào của GCSA."
    },
    {
      id: "fn",
      title: "Bồ Đào Nha và Ma Cao Dr. Francisco Nicolau & Associados",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "Lisbon · Ma Cao",
      address: "Lisbon: Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal; Ma Cao: 1/F, No. 194 Xinma Road, Macau",
      excerpt: "Hãng luật toàn diện tại Lisbon và Ma Cao · hơn ba thập kỷ thực tiễn pháp lý xuất sắc tại Bồ Đào Nha và Ma Cao.",
      focus: ["Luật dân sự", "Luật hình sự", "Tố tụng xuyên biên giới"],
      intro: [
        "Dr. Francisco Nicolau & Associados có văn phòng tại Lisbon và Ma Cao, dựa trên thực tiễn pháp lý xuất sắc hơn ba thập kỷ tại Bồ Đào Nha và Ma Cao, cung cấp tư vấn pháp lý chặt chẽ, chính trực và có tầm nhìn quốc tế cho cá nhân, doanh nghiệp, tổ chức và khu vực công.",
        "Dưới sự dẫn dắt của Dr. Francisco Nicolau—tiến sĩ khoa học pháp lý và luật sư tranh tụng dày dạn—hãng kết hợp nền tảng pháp lý học sâu sắc với 36 năm kinh nghiệm tại tòa, phục vụ bằng tiếng Bồ Đào Nha, Anh, Pháp, Trung và Latin, mang truyền thống civil law chặt chẽ đến khách hàng xuyên biên giới hiện đại.",
        "Lĩnh vực gồm luật dân sự, hình sự, lao động, hành chính, tố tụng dân sự và hiến pháp, với hơn 36 năm kinh nghiệm tranh tụng tại các cấp tòa án Bồ Đào Nha và Ma Cao.",
      ]
    },
    {
      id: "pmt",
      title: "Patrick Mak & Tse Solicitors",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "Hồng Kông · Central",
      address: "Tầng 9, Wing On Centre, 111 Connaught Road Central, Hồng Kông",
      excerpt: "Hãng luật toàn diện gắn với Hồng Kông và hướng ra quốc tế, cung cấp dịch vụ pháp lý đa dạng cả tố tụng và phi tố tụng.",
      focus: ["Tài trợ doanh nghiệp", "Tố tụng thương mại và dân sự", "Công chứng viên được đại lục ủy thác"],
      intro: [
        "Patrick Mak & Tse Solicitors là hãng luật toàn diện có quy mô, gắn với Hồng Kông và hướng ra nước ngoài, cung cấp dịch vụ đa dạng cả tố tụng và phi tố tụng.",
        "Kể từ khi thành lập, hãng có thành tích nổi bật trong tài trợ doanh nghiệp, tố tụng thương mại và dân sự, dịch vụ công chứng viên được đại lục ủy thác, và phục vụ khách hàng nhiều nơi với thái độ thực tế, kiến thức pháp lý chuyên môn và kinh nghiệm ngành sâu.",
        "Năm 2024, Patrick Mak & Tse nhận giải «Dịch vụ cố vấn pháp lý xuất sắc nhất năm Đại Trung Hoa» tại MYTHFOCUS Asia Most Valuable Brand Awards. Giải thưởng cho thấy hãng không chỉ mang dịch vụ và trải nghiệm vượt kỳ vọng khách hàng, mà còn dự đoán và vượt qua vấn đề thương mại, không ngừng nâng năng lực phục vụ và đóng góp cho ngành cùng xã hội.",
        "Trong quá trình phục vụ khách hàng liên tục, hãng đã xây dựng quan hệ nghiệp vụ tốt với cơ quan quản lý liên quan cùng nhiều công ty chứng khoán, ngân hàng đầu tư, hãng kiểm toán, tổ chức thẩm định giá và hãng luật khác trong và ngoài nước. Với kinh nghiệm phối hợp với cơ quan nhà nước và cơ quan quản lý, hãng phát huy thế mạnh, tích hợp nguồn lực để cung cấp dịch vụ pháp lý chất lượng và hiệu quả.",
      ]
    },
    {
      id: "yuanshu",
      title: "Guangdong Prime Partners",
      englishName: "Prime Partners",
      location: "Quảng Châu · Zhujiang New Town",
      address: "Tầng 17, Kaihua International Center, Zhujiang New Town, Tianhe, Quảng Châu",
      excerpt: "Hãng luật boutique toàn diện tập trung vào internet, trí tuệ nhân tạo và kinh tế số, lấy Hoa Nam làm gốc, phủ Vịnh Quảng Đông-Hồng Kông-Ma Cao và vươn ra cả nước.",
      focus: ["Internet", "Trí tuệ nhân tạo", "Kinh tế số"],
      intro: [
        "Guangdong Prime Partners Law Firm (sau đây gọi là «Prime Partners») được thành lập vào tháng 6 năm 2026 bởi một nhóm luật sư cùng chí hướng. Đây là hãng luật boutique toàn diện chuyên về internet, trí tuệ nhân tạo và kinh tế số.",
        "Các thành viên có nền tảng đa dạng và bổ trợ nhau—gồm chủ nhiệm và phó chủ nhiệm ủy ban chuyên môn của hội luật sư cấp tỉnh, thành phố về internet và sở hữu trí tuệ, cùng cựu phụ trách pháp chế từ các công ty internet hàng đầu. Sự kết hợp đó giúp Prime Partners ngay từ đầu vừa có hiểu biết ngành, tầm nhìn quản lý vừa có tư duy thương mại.",
        "Với phương châm «Cội nguồn của pháp luật, logic của con số», Prime Partners đã cung cấp dịch vụ pháp lý cho các doanh nghiệp hàng đầu như ByteDance và Huawei. Lấy Hoa Nam làm gốc, phủ Vịnh lớn và vươn ra cả nước.",
      ]
    }
  ] as const
};

const GCSA_LEGAL_COUNSEL_MS_MY: GcsaLegalCounselSnapshot = {
  title: "Penasihat undang-undang",
  closeModal: "Tutup menu",
  addressLabel: "Cara menghubungi",
  focusLabel: "Tentang kami",
  introTitle: "Perihal Perikatan",
  openIntro: "Pengenalan Perikatan — {name}",
  firms: [
    {
      id: "leelee",
      title: "Singapura Lee & Lee LLP",
      englishName: "Lee & Lee LLP",
      location: "Singapura · North Bridge Road",
      address: "Singapura: 25 North Bridge Road, Level 7, Singapore 179104 (berhampiran Mahkamah Agung)",
      excerpt: "Firma undang-undang perkhidmatan penuh terkemuka Singapura · lebih tujuh dekad sejak ditubuhkan pada 1955, melindungi kepentingan pelanggan dengan integriti, kepakaran dan wawasan strategik.",
      focus: ["Perbankan dan kewangan", "Hal ehwal syarikat", "Harta intelek", "Litigasi dan pertikaian", "Hartanah", "Urusan rentas sempadan"],
      intro: [
        "Lee & Lee ditubuhkan di Singapura pada 1 September 1955 oleh Encik Lee Kuan Yew, Puan Kwa Geok Choo dan Encik Dennis Lee Kim Yew. Pejabatnya kini di Tingkat 7, 25 North Bridge Road. Berakar di Singapura lebih tujuh puluh tahun, firma ini menyediakan perkhidmatan undang-undang yang teliti, berintegriti dan berwawasan antarabangsa kepada individu, perniagaan, institusi kewangan dan badan awam.",
        "Etika profesional yang diasaskan pengasas berterusan hingga kini, menggabungkan amalan common law yang mendalam dengan lebih tujuh puluh tahun pengalaman perbicaraan dan transaksi. Firma berkhidmat kepada pelanggan rentas sempadan dalam bahasa Inggeris dan Cina, dengan falsafah «berpijak pada pengalaman sejarah, selari dengan trend masa depan», membawa penyelesaian undang-undang yang tahan masa untuk perniagaan moden dan pelanggan keluarga.",
        "Bidang amalan merangkumi perbankan dan kewangan, syarikat serta penggabungan dan pengambilalihan, harta intelek, litigasi dan penyelesaian pertikaian, hartanah, serta pelbagai bidang undang-undang khusus. Kini firma telah berkembang menjadi amalan perkhidmatan penuh dengan lebih 180 peguam, paralegal dan kakitangan sokongan, serta pernah menubuhkan perkongsian undang-undang bersama firma antarabangsa yang antara yang paling lama di Singapura, berkhidmat kepada pelanggan keluarga merentas lebih tiga generasi serta banyak syarikat tersenarai dan kumpulan multinasional.",
      ],
      disclaimer: "Hubungan penasihat undang-undang tetap yang dinyatakan di atas tidak merupakan sebarang pengendorsan, sokongan, kelulusan, pengesahan, jaminan, waranti, representasi atau komitmen oleh Lee & Lee LLP terhadap perniagaan, aktiviti, operasi, produk atau perkhidmatan GCSA, dan tidak bermaksud Lee & Lee LLP terlibat secara langsung dalam mana-mana perniagaan, aktiviti, operasi, produk atau perkhidmatan GCSA."
    },
    {
      id: "fn",
      title: "Portugal & Macau Dr. Francisco Nicolau & Associados",
      englishName: "Dr. Francisco Nicolau & Associados",
      location: "Lisbon · Macau",
      address: "Lisbon: Rua Manuela Porto, No. 10, 2º Esq., 1500-422 Lisbon, Portugal; Macau: 1/F, No. 194 Xinma Road, Macau",
      excerpt: "Firma perkhidmatan penuh di Lisbon dan Macau · lebih tiga dekad amalan undang-undang yang cemerlang merentas Portugal dan Macau.",
      focus: ["Undang-undang sivil", "Undang-undang jenayah", "Litigasi rentas sempadan"],
      intro: [
        "Dr. Francisco Nicolau & Associados mempunyai pejabat di Lisbon dan Macau, berakar pada amalan undang-undang cemerlang lebih tiga dekad merentas Portugal dan Macau, memberikan nasihat undang-undang yang teliti, berintegriti dan berwawasan antarabangsa kepada individu, perniagaan, institusi dan sektor awam.",
        "Di bawah pimpinan Dr. Francisco Nicolau—doktor sains undang-undang dan peguam bicara berpengalaman—firma menggabungkan ilmu undang-undang yang mendalam dengan 36 tahun pengalaman perbicaraan, berkhidmat dalam bahasa Portugis, Inggeris, Perancis, Cina dan Latin, membawa tradisi civil law yang teliti kepada pelanggan rentas sempadan moden.",
        "Bidang amalan merangkumi undang-undang sivil, jenayah, buruh, pentadbiran, prosiding sivil dan perlembagaan, dengan lebih 36 tahun pengalaman berbicaraan di semua peringkat mahkamah Portugal dan Macau.",
      ]
    },
    {
      id: "pmt",
      title: "Patrick Mak & Tse Solicitors",
      englishName: "Patrick Mak & Tse Solicitors",
      location: "Hong Kong · Central",
      address: "Tingkat 9, Wing On Centre, 111 Connaught Road Central, Hong Kong",
      excerpt: "Firma perkhidmatan penuh yang berakar di Hong Kong dan berpandangan antarabangsa, menyediakan perkhidmatan undang-undang yang pelbagai merangkumi litigasi dan bukan litigasi.",
      focus: ["Pembiayaan korporat", "Litigasi komersial dan sivil", "Notari yang dilantik tanah besar China"],
      intro: [
        "Patrick Mak & Tse Solicitors ialah firma undang-undang perkhidmatan penuh berskala yang berakar di Hong Kong dan berpandangan ke luar negara, mampu menyediakan perkhidmatan pelbagai merangkumi litigasi dan bukan litigasi.",
        "Sejak ditubuhkan, firma ini mempunyai rekod kukuh dalam pembiayaan korporat, litigasi komersial dan sivil serta perkhidmatan notari yang dilantik tanah besar China, dan berkhidmat kepada pelanggan di pelbagai tempat dengan sikap praktikal, pengetahuan undang-undang pakar dan pengalaman industri yang mendalam.",
        "Pada 2024, Patrick Mak & Tse menerima anugerah «Perkhidmatan Penasihat Undang-undang Terbaik Tahun Greater China» dalam MYTHFOCUS Asia Most Valuable Brand Awards. Anugerah itu menunjukkan perkhidmatan dan pengalaman yang melebihi jangkaan pelanggan, serta keupayaan menjangka dan mengatasi masalah komersial, terus meningkatkan mutu perkhidmatan dan menyumbang kepada profesion serta masyarakat.",
        "Melalui perkhidmatan pelanggan yang berterusan, firma membina hubungan baik dengan pengawal selia berkaitan serta banyak syarikat sekuriti, bank pelaburan, firma perakaunan, institusi penilaian dan firma undang-undang lain di dalam dan luar negara. Dengan pengalaman bekerjasama dengan badan kerajaan dan pengawal selia, firma menyelaraskan sumber untuk memberikan nasihat undang-undang yang berkualiti dan cekap.",
      ]
    },
    {
      id: "yuanshu",
      title: "Guangdong Prime Partners",
      englishName: "Prime Partners",
      location: "Guangzhou · Zhujiang New Town",
      address: "Tingkat 17, Kaihua International Center, Zhujiang New Town, Tianhe, Guangzhou",
      excerpt: "Firma butik komprehensif yang menumpukan internet, kecerdasan buatan dan ekonomi digital—berakar di China Selatan, merangkumi Greater Bay Area dan meluas ke seluruh negara.",
      focus: ["Internet", "Kecerdasan buatan", "Ekonomi digital"],
      intro: [
        "Guangdong Prime Partners Law Firm (selepas ini «Prime Partners») ditubuhkan pada Jun 2026 oleh sekumpulan peguam yang sehaluan. Ia ialah firma butik komprehensif yang menumpukan internet, kecerdasan buatan dan ekonomi digital.",
        "Rakan kongsi mempunyai latar yang pelbagai dan saling melengkapi—termasuk pengerusi dan timbalan pengerusi jawatankuasa profesional persatuan peguam wilayah dan bandar dalam internet serta harta intelek, juga bekas ketua undang-undang syarikat internet terkemuka. Gabungan itu memberi Prime Partners wawasan industri, perspektif kawal selia dan pemikiran perniagaan sejak awal.",
        "Dengan falsafah «Asal Undang-undang, Logik Nombor», Prime Partners telah memberikan perkhidmatan undang-undang kepada perusahaan terkemuka termasuk ByteDance dan Huawei. Berpangkal di China Selatan, merangkumi Greater Bay Area dan meluas ke seluruh negara.",
      ]
    }
  ] as const
};
export const GCSA_LEGAL_COUNSEL_BY_LOCALE: Record<
  SiteFooterLocale,
  GcsaLegalCounselSnapshot
> = {
  "zh-CN": GCSA_LEGAL_COUNSEL_ZH_CN,
  "zh-TW": GCSA_LEGAL_COUNSEL_ZH_TW,
  "en-US": GCSA_LEGAL_COUNSEL_EN_US,
  "th-TH": GCSA_LEGAL_COUNSEL_TH_TH,
  "id-ID": GCSA_LEGAL_COUNSEL_ID_ID,
  "ko-KR": GCSA_LEGAL_COUNSEL_KO_KR,
  "ja-JP": GCSA_LEGAL_COUNSEL_JA_JP,
  "vi-VN": GCSA_LEGAL_COUNSEL_VI_VN,
  "ms-MY": GCSA_LEGAL_COUNSEL_MS_MY
};

/** 默认：简体中文完整复刻 */
export const GCSA_LEGAL_COUNSEL_DEFAULT = GCSA_LEGAL_COUNSEL_ZH_CN;

export function resolveGcsaLegalCounsel(
  locale: SiteFooterLocale = 'zh-CN'
): GcsaLegalCounselSnapshot {
  return GCSA_LEGAL_COUNSEL_BY_LOCALE[locale] ?? GCSA_LEGAL_COUNSEL_EN_US;
}
